import { DestroyRef, Injectable, effect, inject, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { LoggerService } from '@core/services/infra/logger.service';;
import { BehaviorSubject, Observable } from 'rxjs';
import { Firestore, collection, query, where, getDocs, doc, getDoc, setDoc, onSnapshot, CollectionReference, DocumentData, Unsubscribe } from '@angular/fire/firestore';
import { Auth, authState, User as FirebaseUser } from '@angular/fire/auth';
import { Functions, httpsCallable } from '@angular/fire/functions';
import { WorkspaceMembershipModel } from '@shared/definitions/workspace-membership';
import { WorkspaceRole, WorkspaceType } from '@shared/definitions/workspace';
import { WorkspaceStore } from '@shared/services/workspace.store';
import { SessionService } from '@core/services/auth/session.service';
import { isPermissionDeniedError } from '@core/services/auth/auth-transition-error';
import { SerialQueue } from '@shared/utils/serial-queue';
import {
  Workspace,
  CreatePersonalWorkspaceResponse,
  UpdateWorkspaceStatsResponse,
  WorkspaceDeletionImpact,
  WorkspaceDeletionImpactResponse,
  SoftDeleteWorkspaceResponse,
  PermanentlyDeleteWorkspaceResponse,
} from '@core/services/auth/workspace.service.types';
import {
  isFreshSignupFromMetadata,
  buildWorkspaceFromData,
  buildWorkspaceListEntryFromData,
  skipsPersonalWorkspaceFallback,
  isSoftDeletedWorkspace,
  pickAutoSelectWorkspace,
} from '@core/services/auth/workspace.service.helpers';

// Re-export the extracted module-level types and helpers so existing
// `import { … } from '@core/services/auth/workspace.service'` call-sites keep working unchanged.
export * from '@core/services/auth/workspace.service.types';
export * from '@core/services/auth/workspace.service.helpers';

@Injectable({
  providedIn: 'root'
})
export class WorkspaceService {
  private readonly CONTEXT = 'WorkspaceService';

  private readonly destroyRef = inject(DestroyRef);

  private currentWorkspaceIdSubject = new BehaviorSubject<string | null>(null);
  private currentWorkspaceSubject = new BehaviorSubject<Workspace | null>(null);
  private userWorkspacesSubject = new BehaviorSubject<Workspace[]>([]);
  private initialized = false;
  private initializationStarted = false;
  private initializationComplete = false;
  private workspaceUnsubscribe: Unsubscribe | null = null;

  // Race condition protection: prevent concurrent switch operations.
  // `switchInProgress` lets `loadUserWorkspaces` skip auto-selection while a
  // switch is mid-flight; `switchQueue` serializes the switches themselves so
  // each caller's `await` resolves only once ITS workspace is fully active and
  // persisted (the fix for the "had to switch twice" reload race).
  private switchInProgress = false;
  private readonly switchQueue = new SerialQueue();

  public currentWorkspaceId$: Observable<string | null> = this.currentWorkspaceIdSubject.asObservable();
  public currentWorkspace$: Observable<Workspace | null> = this.currentWorkspaceSubject.asObservable();
  public userWorkspaces$: Observable<Workspace[]> = this.userWorkspacesSubject.asObservable();

  // Phase 2 — signal projection of the same state. Mirrored via `emit*`
  // helpers so callers can pick the paradigm that suits them; backward
  // compatibility is preserved.
  private readonly _currentWorkspaceIdSig = signal<string | null>(null);
  private readonly _currentWorkspaceSig = signal<Workspace | null>(null);
  private readonly _userWorkspacesSig = signal<Workspace[]>([]);

  readonly currentWorkspaceIdSignal = this._currentWorkspaceIdSig.asReadonly();
  readonly currentWorkspaceSignal = this._currentWorkspaceSig.asReadonly();
  readonly userWorkspacesSignal = this._userWorkspacesSig.asReadonly();

  // Note: For membership models, use getUserWorkspacesMemberships() method instead of observable

  private firestore = inject(Firestore);
  private auth = inject(Auth);
  private functions = inject(Functions);
  private session = inject(SessionService);

  private readonly workspaceStore = inject(WorkspaceStore);

  constructor(private logger: LoggerService) {
    this.bridgeToStore();
    // Don't initialize in constructor - wait until first use to avoid Firebase init issues
  }

  /**
   * Mirror the writable signals into `WorkspaceStore` so consumers can
   * depend on the store instead of the service for state reads.
   * Follows the same pattern as `AuthService.bridgeBehaviorSubjectsToStore`.
   */
  private bridgeToStore(): void {
    effect(() => {
      this.workspaceStore.setCurrentWorkspaceId(this._currentWorkspaceIdSig());
    });
    effect(() => {
      this.workspaceStore.setCurrentWorkspace(this._currentWorkspaceSig());
    });
    effect(() => {
      this.workspaceStore.setUserWorkspaces(this._userWorkspacesSig());
    });
  }

  /**
   * Adopt a workspace id straight from the ID token's `workspaces` claim,
   * before any Firestore round-trip has happened.
   *
   * Normally the id is only discovered by `loadUserWorkspaces()` — a
   * `where('members.<uid>', '!=', null)` query — followed by
   * `setCurrentWorkspace()`'s document read. Every workspace-scoped listener
   * waits on that pair through `waitForWorkspace()`, which polls in 100ms
   * steps: measured at 1332ms of dead waiting on a cold load (#234). The token
   * already carries the id at that point, so this publishes it immediately.
   *
   * Only the ID is published — deliberately NOT the `Workspace` object, which
   * still requires the read, and NOT `session.storeCurrentWorkspaceId`, because
   * nothing has been verified yet. `setCurrentWorkspace()` remains the only
   * path that persists a selection.
   *
   * No-op when a workspace is already selected, so a session-restored or
   * user-switched id is never overridden. The caller (AuthService) additionally
   * restricts this to tokens carrying exactly one workspace, since choosing
   * between several belongs to the persisted preference.
   *
   * @returns true when the id was adopted.
   */
  adoptWorkspaceIdFromClaims(workspaceId: string): boolean {
    if (this.currentWorkspaceIdSubject.value) return false;

    this.logger.debug(`[workspace] 🎫 Adopting workspace ${workspaceId} from token claims`, this.CONTEXT);
    this.currentWorkspaceIdSubject.next(workspaceId);
    this._currentWorkspaceIdSig.set(workspaceId);
    return true;
  }

  /**
   * Get current workspace ID
   * Throws error if no workspace is selected
   */
  get currentWorkspaceId(): string {
    this.ensureInitialized(); // Lazy initialization
    const workspaceId = this.currentWorkspaceIdSubject.value;
    if (!workspaceId) {
      throw new Error('No workspace selected. Please select a workspace first.');
    }
    return workspaceId;
  }

  /**
   * Get current workspace ID or null (safe version)
   */
  get currentWorkspaceIdOrNull(): string | null {
    this.ensureInitialized(); // Lazy initialization
    return this.currentWorkspaceIdSubject.value;
  }

  /**
   * Get current workspace data
   */
  get currentWorkspace(): Workspace | null {
    this.ensureInitialized(); // Lazy initialization
    return this.currentWorkspaceSubject.value;
  }

  /**
   * Get user's role in current workspace
   */
  async getCurrentUserRole(): Promise<string | null> {
    this.ensureInitialized(); // Lazy initialization
    const workspace = this.currentWorkspace;
    const user = this.auth.currentUser;
    
    if (!workspace || !user) {
      return null;
    }

    const member = workspace.members[user.uid];
    return member?.role || null;
  }

  /**
   * Check if user has required role in current workspace
   */
  async hasRole(requiredRoles: string[]): Promise<boolean> {
    const role = await this.getCurrentUserRole();
    return role ? requiredRoles.includes(role) : false;
  }

  /**
   * Check if user can manage workspace (owner/admin only)
   */
  async canManageWorkspace(): Promise<boolean> {
    return this.hasRole(['owner', 'admin']);
  }

  /**
   * Check if user can modify data (not viewer)
   */
  async canModifyData(): Promise<boolean> {
    return this.hasRole(['owner', 'admin', 'manager', 'standard']);
  }

  /**
   * Initialize workspace from storage on app start
   * This is called lazily on first access to avoid Firebase init issues
   */
  private async initializeWorkspace(): Promise<void> {
    // Only initialize once
    if (this.initializationStarted) {
      return;
    }
    this.initializationStarted = true;

    const workspaceId = this.session.getCurrentWorkspaceId();
    if (workspaceId) {
      try {
        this.logger.debug(`[workspace] 🔄 Restoring workspace from session: ${workspaceId}`, this.CONTEXT);
        await this.setCurrentWorkspace(workspaceId);
        this.logger.debug(`[workspace] ✅ Workspace restored from session`, this.CONTEXT);
      } catch (error) {
        this.logger.warn('Could not restore workspace from session:', this.CONTEXT, error);
        this.session.clearCurrentWorkspaceId();
      }
    }

    // Mark initialization as complete AFTER session restoration
    this.initializationComplete = true;

    // Load user's workspaces when authenticated (app-lifetime stream; belt for the leak checker)
    authState(this.auth).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async (user: FirebaseUser | null) => {
      if (user) {
        await this.loadUserWorkspaces(user.uid);
      } else {
        this.clearWorkspace();
      }
    });
  }

  /**
   * Check if initialization is complete
   */
  isInitializationComplete(): boolean {
    return this.initializationComplete;
  }

  /**
   * Ensure initialization has started (called lazily by getters/methods)
   */
  private ensureInitialized(): void {
    if (!this.initializationStarted) {
      this.initializeWorkspace().catch(err => {
        this.logger.error('Failed to initialize workspace:', this.CONTEXT, err);
      });
    }
  }

  /**
   * Whether this account opted out of the auto-created personal workspace.
   *
   * Set on `users/{uid}` by the tenant and service-provider invitation signups.
   * Read here so the "no workspace found" fallback can tell a genuine trigger
   * failure apart from an invited signup that is simply between two steps.
   *
   * Fails OPEN: if the document can't be read (permission, offline), we return
   * false and let the fallback run. Recreating a workspace is recoverable;
   * leaving a real owner with none is not.
   */
  private async shouldSkipPersonalWorkspace(userId: string): Promise<boolean> {
    try {
      const snap = await getDoc(doc(this.firestore, `users/${userId}`));
      return skipsPersonalWorkspaceFallback(snap.exists() ? snap.data() : null);
    } catch (error) {
      this.logger.warn(
        '[workspace] could not read skipPersonalWorkspace, letting the fallback run',
        this.CONTEXT,
        error,
      );
      return false;
    }
  }

  /**
   * Load all workspaces where user is a member
   * Includes retry logic for new users whose workspace might still be creating.
   * Returning users get a single short retry — only signup races need the longer 3×2s loop.
   */
  async loadUserWorkspaces(userId: string, retryCount: number = 0): Promise<void> {
    // New signup: creationTime is within ~60s of lastSignInTime → CF may still be creating workspace.
    const isFreshSignup = isFreshSignupFromMetadata(this.auth.currentUser?.metadata);

    const MAX_RETRIES = isFreshSignup ? 3 : 1;
    const RETRY_DELAY = isFreshSignup ? 2000 : 1000;

    this.logger.debug(`[workspace] 🔄 Loading workspaces for user ${userId} (attempt ${retryCount + 1}/${MAX_RETRIES + 1}, freshSignup=${isFreshSignup})`, this.CONTEXT);
    
    try {
      this.logger.debug(`[workspace] 📝 Building Firestore query...`, this.CONTEXT);
      const workspacesRef = collection(this.firestore, 'workspaces') as CollectionReference<DocumentData>;
      const q = query(workspacesRef, where(`members.${userId}`, '!=', null));
      
      this.logger.debug(`[workspace] 🔍 Executing query...`, this.CONTEXT);
      const snapshot = await getDocs(q);
      
      this.logger.debug(`[workspace] 📦 Query returned ${snapshot.size} workspace(s)`, this.CONTEXT);

      // Filtré en mémoire, PAS par une clause `where('deletedAt', '==', null)` :
      // Firestore écarte les documents où le champ filtré est absent, et aucun
      // espace sain ne porte `deletedAt` — la clause les ferait tous
      // disparaître. Le tri se fait donc après lecture.
      const workspaces: Workspace[] = [];
      let hiddenAsDeleted = 0;
      snapshot.forEach(docSnap => {
        const data = docSnap.data();
        if (isSoftDeletedWorkspace(data)) {
          hiddenAsDeleted++;
          return;
        }
        workspaces.push(buildWorkspaceListEntryFromData(docSnap.id, data));
      });
      if (hiddenAsDeleted > 0) {
        this.logger.debug(
          `[workspace] 🗑️ ${hiddenAsDeleted} espace(s) supprimé(s) masqué(s)`,
          this.CONTEXT,
        );
      }

      // If no workspaces found and we haven't exceeded retries, wait and retry
      // This handles the race condition where cloud function is still creating the workspace
      if (workspaces.length === 0 && retryCount < MAX_RETRIES) {
        this.logger.warn(`[workspace] ⏳ No workspaces found (attempt ${retryCount + 1}/${MAX_RETRIES}), this.CONTEXT, retrying in ${RETRY_DELAY}ms...`);
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
        return this.loadUserWorkspaces(userId, retryCount + 1);
      }

      // FALLBACK: If still no workspaces after all retries, manually create one.
      //
      // NOT on the invited paths (tenant, service provider). There, zero
      // workspaces is the NORMAL state, not a failed trigger: the signup set
      // `skipPersonalWorkspace` so `onUserCreatedWorkspace` deliberately skips
      // the personal workspace, and the dedicated one is provisioned a moment
      // later by `createTenantWorkspace` / `createServiceProviderWorkspace`.
      // Without this guard the safety net mistook that silence for a breakdown
      // and called `createPersonalWorkspace`, recreating the very workspace the
      // flag asked to avoid — an orphan space, and the trigger's own log said
      // "Skipping personal workspace" while it appeared anyway (#300).
      if (workspaces.length === 0 && await this.shouldSkipPersonalWorkspace(userId)) {
        this.logger.debug(
          '[workspace] invited signup — no workspace yet is expected, skipping the fallback',
          this.CONTEXT,
        );
        this.userWorkspacesSubject.next(workspaces);
        this._userWorkspacesSig.set(workspaces);
        return;
      }

      if (workspaces.length === 0) {
        this.logger.warn('[workspace] 🔧 No workspaces found after retries - attempting manual creation as fallback', this.CONTEXT);
        try {
          const createPersonalWorkspace = httpsCallable<unknown, CreatePersonalWorkspaceResponse>(
            this.functions,
            'createPersonalWorkspace'
          );
          const result = await createPersonalWorkspace({});

          if (result.data.success) {
            this.logger.debug(`[workspace] ✅ Fallback workspace created: ${result.data.workspaceId}`, this.CONTEXT);
            // Retry loading workspaces once more
            await new Promise(resolve => setTimeout(resolve, 1000));
            return this.loadUserWorkspaces(userId, MAX_RETRIES); // Use MAX_RETRIES to prevent further retries
          }
        } catch (fallbackError) {
          this.logger.error('[workspace] ❌ Fallback workspace creation failed:', this.CONTEXT, fallbackError);
          this.logger.error('[workspace] 💡 User should contact support - this is a critical issue', this.CONTEXT);
        }
      }

      this.userWorkspacesSubject.next(workspaces);
      this._userWorkspacesSig.set(workspaces);

      // WORKSPACE v2.3: Smart auto-selection with user preferences
      // - Always try to restore from session first (current browser tab)
      // - Then try last active workspace from user preferences
      // - Fall back to personal workspace if available
      // - Only for users with a single workspace, auto-select immediately
      this.logger.debug(`[workspace] 📋 Loaded ${workspaces.length} workspace(s) for user`, this.CONTEXT);

      // RACE CONDITION FIX: Don't auto-select if a switch is already in progress
      // or if a workspace is already selected
      if (this.switchInProgress) {
        this.logger.debug(`[workspace] ⏳ Switch in progress, skipping auto-selection`, this.CONTEXT);
        return;
      }

      if (!this.currentWorkspaceIdSubject.value && workspaces.length > 0) {
        // "Open in new tab" hand-off from the workspace switcher: a different
        // tab stashed a target id in localStorage before window.open(). Honor
        // it once and clear the key so a subsequent reload doesn't re-switch.
        try {
          const pending = localStorage.getItem('proporio_pending_workspace_switch');
          if (pending && workspaces.some(w => w.id === pending)) {
            localStorage.removeItem('proporio_pending_workspace_switch');
            this.logger.debug(`[workspace] 🆕 Honoring new-tab pending switch: ${pending}`, this.CONTEXT);
            await this.setCurrentWorkspace(pending);
            return;
          }
          if (pending) localStorage.removeItem('proporio_pending_workspace_switch');
        } catch { /* localStorage unavailable — continue with normal selection */ }

        // Race-condition guard: sessionStorage may already hold the workspace
        // a sibling `setCurrentWorkspace()` is currently establishing. Its
        // BehaviorSubject hasn't caught up yet because it's awaiting the
        // listener's first snapshot, so the check above thinks no workspace
        // is selected. Without this
        // guard we'd run `loadLastActiveWorkspace` and select a (possibly
        // stale) workspace that then competes with the in-flight restore —
        // honor the already-persisted browser selection instead.
        const sessionWorkspaceId = this.session.getCurrentWorkspaceId();
        if (sessionWorkspaceId && workspaces.some(w => w.id === sessionWorkspaceId)) {
          this.logger.debug(
            `[workspace] ⏭️ sessionStorage already holds ${sessionWorkspaceId}; skipping last-active lookup`,
            this.CONTEXT
          );
          return;
        }

        // Last active workspace from user preferences, then the fallbacks —
        // the whole decision lives in `pickAutoSelectWorkspace` (pure, tested).
        // A preference that leads into an abandoned wizard is bypassed in
        // favour of a finished workspace (prod, 2026-09-14). Auto-selections
        // never persist the preference: `setCurrentWorkspace` without
        // `persistPreference` leaves `lastActiveWorkspaceId` untouched.
        const lastActiveWorkspaceId = await this.loadLastActiveWorkspace();
        const pick = pickAutoSelectWorkspace(workspaces, lastActiveWorkspaceId, userId);

        if (pick) {
          if (pick.reason !== 'last-active' && lastActiveWorkspaceId &&
              workspaces.some(w => w.id === lastActiveWorkspaceId)) {
            this.logger.debug(
              `[workspace] ⏭️ Last active ${lastActiveWorkspaceId} is stuck in onboarding; ` +
              `auto-selecting ${pick.id} (${pick.reason}) instead`,
              this.CONTEXT,
            );
          } else {
            this.logger.debug(`[workspace] 🔄 Auto-selecting workspace ${pick.id} (${pick.reason})`, this.CONTEXT);
          }
          await this.setCurrentWorkspace(pick.id);
        } else {
          // Multiple workspaces and no clear default - let user choose
          this.logger.debug(`[workspace] ⏸️ Multiple workspaces available, no default found - user must choose`, this.CONTEXT);
        }
      } else if (workspaces.length === 0) {
        this.logger.error('[workspace] ❌ No workspaces found for user even after fallback attempt', this.CONTEXT);
        this.logger.error('[workspace] 💡 Critical issue - user should contact support', this.CONTEXT);
      } else {
        this.logger.debug(`[workspace] ✅ Workspace already selected: ${this.currentWorkspaceIdSubject.value}`, this.CONTEXT);
      }
    } catch (error: unknown) {
      const errObj = error as { constructor?: { name?: string }; message?: string; code?: string } | null;
      // Permission-denied during auth transitions (logout/login of a different
      // account) is expected — the listener fires its final error before the
      // unsubscribe + new claim land. Silence the verbose 5-line dump.
      if (!isPermissionDeniedError(error)) {
        this.logger.error('[workspace] ❌ ERROR loading user workspaces:', this.CONTEXT, error);
        this.logger.error('[workspace] ❌ Error type:', this.CONTEXT, errObj?.constructor?.name);
        this.logger.error('[workspace] ❌ Error message:', this.CONTEXT, errObj?.message);
        this.logger.error('[workspace] ❌ Error code:', this.CONTEXT, errObj?.code);
        this.logger.error('[workspace] ❌ Full error:', this.CONTEXT, JSON.stringify(error, null, 2));
      }

      // Retry on error if we haven't exceeded max retries
      if (retryCount < MAX_RETRIES) {
        this.logger.warn(`[workspace] 🔄 Retrying workspace load after error (attempt ${retryCount + 1}/${MAX_RETRIES})...`, this.CONTEXT);
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
        return this.loadUserWorkspaces(userId, retryCount + 1);
      } else {
        this.logger.error('[workspace] ❌ Max retries exceeded, this.CONTEXT, giving up');
        throw error; // Re-throw after max retries
      }
    }
  }

  /**
   * Save last active workspace to user preferences
   */
  private async saveLastActiveWorkspace(workspaceId: string): Promise<void> {
    const user = this.auth.currentUser;
    if (!user) return;

    try {
      const prefsRef = doc(this.firestore, `users/${user.uid}/preferences/workspace`);
      await setDoc(prefsRef, {
        lastActiveWorkspaceId: workspaceId,
        updatedAt: new Date()
      }, { merge: true });
      this.logger.debug(`[workspace] 💾 Saved last active workspace: ${workspaceId}`, this.CONTEXT);
    } catch (error) {
      this.logger.warn('[workspace] ⚠️ Failed to save last active workspace:', this.CONTEXT, error);
      // Don't throw - this is not critical
    }
  }

  /**
   * Load last active workspace from user preferences
   */
  private async loadLastActiveWorkspace(): Promise<string | null> {
    const user = this.auth.currentUser;
    if (!user) return null;

    try {
      const prefsRef = doc(this.firestore, `users/${user.uid}/preferences/workspace`);
      const prefsDoc = await getDoc(prefsRef);
      
      if (prefsDoc.exists()) {
        const data = prefsDoc.data();
        const lastWorkspaceId = data['lastActiveWorkspaceId'];
        this.logger.debug(`[workspace] 📖 Loaded last active workspace: ${lastWorkspaceId}`, this.CONTEXT);
        return lastWorkspaceId || null;
      }
    } catch (error) {
      this.logger.warn('[workspace] ⚠️ Failed to load last active workspace:', this.CONTEXT, error);
    }
    
    return null;
  }

  /**
   * Set current workspace and load its data with real-time updates.
   *
   * Switches are serialized through `switchQueue`, so when several callers race
   * (app bootstrap restore + auth `reinitialize` + a user click) each one's
   * `await` resolves only once ITS workspace is actually active and persisted —
   * never early. This is what makes the toolbar's "persist then hard-reload"
   * flow reliable on the first click.
   *
   * @param workspaceId target workspace.
   * @param persistPreference when `true`, also record this workspace as the
   *   user's "last active" cross-device default (used to pick the landing
   *   workspace on the next login). Only EXPLICIT user switches should set
   *   this; auto-restore / fallback selections pass `false` so a transient
   *   fallback (e.g. a missed preference read) can never clobber the stored
   *   choice and silently demote the user back to their personal workspace.
   * @returns a promise that resolves when the workspace data is fully loaded
   *   and persisted.
   */
  async setCurrentWorkspace(workspaceId: string, persistPreference = false): Promise<void> {
    return this.switchQueue.run(() => this.applyCurrentWorkspace(workspaceId, persistPreference));
  }

  private async applyCurrentWorkspace(workspaceId: string, persistPreference: boolean): Promise<void> {
    this.logger.debug(`[workspace] 🎯 Setting current workspace to: ${workspaceId}`, this.CONTEXT);

    this.switchInProgress = true;

    try {
      // Clean up any existing listener
      if (this.workspaceUnsubscribe) {
        this.logger.debug(`[workspace] 🧹 Cleaning up previous workspace listener`, this.CONTEXT);
        this.workspaceUnsubscribe();
        this.workspaceUnsubscribe = null;
      }

      const docRef = doc(this.firestore, 'workspaces', workspaceId);

      // #441 lot 1: the first `onSnapshot` callback delivers exactly what the
      // old one-time `getDoc` verified (existence, soft-delete), so the two
      // round trips are merged into one — `getDoc` was a pure network cost
      // added on top of the listener that immediately follows it.
      await new Promise<void>((resolve, reject) => {
        let settled = false;

        this.workspaceUnsubscribe = onSnapshot(
          docRef,
          (docSnap) => {
            if (!settled) {
              settled = true;

              if (!docSnap.exists()) {
                this.logger.error(`[workspace] ❌ Workspace ${workspaceId} not found`, this.CONTEXT);
                this.workspaceUnsubscribe?.();
                this.workspaceUnsubscribe = null;
                reject(new Error('Workspace not found'));
                return;
              }

              const data = docSnap.data();
              // Un espace supprimé est refusé comme un espace absent. Sans ça, la
              // restauration de session le ressuscite au rechargement suivant : l'id
              // est encore en session et le document existe encore pendant 30 jours.
              // L'appelant vide alors l'id de session et repart sur un autre espace.
              if (isSoftDeletedWorkspace(data)) {
                this.logger.warn(
                  `[workspace] 🗑️ Workspace ${workspaceId} est supprimé — sélection refusée`,
                  this.CONTEXT,
                );
                this.workspaceUnsubscribe?.();
                this.workspaceUnsubscribe = null;
                reject(new Error('Workspace deleted'));
                return;
              }

              // CRITICAL FIX: Persist SYNCHRONOUSLY before resolving
              // This ensures page reloads will use the correct workspace ID
              this.session.storeCurrentWorkspaceId(workspaceId);
              this.currentWorkspaceIdSubject.next(workspaceId);
              this._currentWorkspaceIdSig.set(workspaceId);
              this.logger.debug(`[workspace] 💾 Workspace ID persisted: ${workspaceId}`, this.CONTEXT);

              const initialWorkspace: Workspace = buildWorkspaceFromData(docSnap.id, data);
              this.currentWorkspaceSubject.next(initialWorkspace);
              this._currentWorkspaceSig.set(initialWorkspace);
              this.initialized = true;
              this.logger.debug(`[workspace] ✅ Initial workspace data loaded: "${initialWorkspace.name}"`, this.CONTEXT);

              resolve();
              return;
            }

            // Subsequent real-time updates.
            if (!docSnap.exists()) {
              this.logger.error(`[workspace] ❌ Workspace ${workspaceId} no longer exists`, this.CONTEXT);
              return;
            }

            const updatedData = docSnap.data();
            const workspace: Workspace = buildWorkspaceFromData(docSnap.id, updatedData);

            this.logger.debug(`[workspace] 🔄 Workspace updated: "${workspace.name}" (${workspace.type})`, this.CONTEXT);

            // Only broadcast if this is still the current workspace (prevent stale updates)
            if (this.currentWorkspaceIdSubject.value === workspaceId) {
              this.currentWorkspaceSubject.next(workspace);
              this._currentWorkspaceSig.set(workspace);
              this.logger.debug(`[workspace] ✅ Workspace data broadcasted to subscribers`, this.CONTEXT);
            }
          },
          (error) => {
            this.logger.error('[workspace] ❌ Error in workspace real-time listener:', this.CONTEXT, error);
            if (!settled) {
              settled = true;
              this.workspaceUnsubscribe = null;
              reject(error instanceof Error ? error : new Error(String(error)));
            }
          }
        );
      });

      // Record as the cross-device "last active" default only for explicit
      // user switches — see the `persistPreference` doc on setCurrentWorkspace.
      // Awaited (not fire-and-forget) so the preference is durably written
      // BEFORE the caller proceeds: the toolbar hard-reloads right after
      // `switchWorkspace()` resolves, which would otherwise abort the in-flight
      // Firestore write and lose the new default. `saveLastActiveWorkspace`
      // swallows its own errors, so this never breaks the switch.
      if (persistPreference) {
        await this.saveLastActiveWorkspace(workspaceId);
      }

      this.logger.debug(`[workspace] ✅ Real-time workspace listener established`, this.CONTEXT);
    } catch (error) {
      // Silence permission-denied during auth transitions; rethrow so callers
      // can recover (the Firestore listener will re-attempt with fresh claims).
      if (!isPermissionDeniedError(error)) {
        this.logger.error('[workspace] ❌ Error setting current workspace:', this.CONTEXT, error);
      }
      throw error;
    } finally {
      this.switchInProgress = false;
    }
  }

  /**
   * Clear current workspace selection and clean up listeners
   */
  clearWorkspace(): void {
    // Clean up real-time listener
    if (this.workspaceUnsubscribe) {
      this.logger.debug(`[workspace] 🧹 Cleaning up workspace listener on clear`, this.CONTEXT);
      this.workspaceUnsubscribe();
      this.workspaceUnsubscribe = null;
    }
    
    this.currentWorkspaceIdSubject.next(null);
    this._currentWorkspaceIdSig.set(null);
    this.currentWorkspaceSubject.next(null);
    this._currentWorkspaceSig.set(null);
    this.userWorkspacesSubject.next([]);
    this._userWorkspacesSig.set([]);
    this.initialized = false;
    this.session.clearCurrentWorkspaceId();
  }

  /**
   * Validate workspace ID is available
   * Throws error if not selected
   */
  requireWorkspaceId(): string {
    this.ensureInitialized(); // Lazy initialization
    const workspaceId = this.currentWorkspaceIdSubject.value;
    if (!workspaceId) {
      throw new Error('No workspace selected. Please select a workspace first.');
    }
    return workspaceId;
  }

  /**
   * Get current workspace ID (method version for compatibility)
   * Returns null if no workspace selected
   */
  getCurrentWorkspaceId(): string | null {
    this.ensureInitialized(); // Lazy initialization
    return this.currentWorkspaceIdSubject.value;
  }

  /**
   * Wait for workspace to be initialized (with timeout)
   * @param timeout Timeout in milliseconds. Default 4s — long enough to ride out
   *   a normal logout/login transition, short enough that 9+ services calling
   *   this in parallel can't compound into a minute-long wait if something
   *   upstream is genuinely stuck.
   * @returns Promise<string> The workspace ID when available
   */
  async waitForWorkspace(timeout: number = 4000): Promise<string> {
    this.ensureInitialized(); // Trigger initialization if needed
    const startTime = Date.now();

    while (!this.currentWorkspaceIdSubject.value) {
      if (Date.now() - startTime > timeout) {
        throw new Error('Timeout waiting for workspace selection');
      }
      // Wait 100ms before checking again
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    return this.currentWorkspaceIdSubject.value;
  }

  /**
   * Switch to a different workspace (explicit user/flow action).
   *
   * Unlike the internal auto-restore selections, this records the target as
   * the user's "last active" workspace so the next login lands here.
   * @param workspaceId The workspace ID to switch to
   */
  async switchWorkspace(workspaceId: string): Promise<void> {
    await this.setCurrentWorkspace(workspaceId, /* persistPreference */ true);
  }

  /**
   * Reinitialize workspace (reload user workspaces and current workspace)
   */
  async reinitialize(): Promise<void> {
    const user = this.auth.currentUser;
    if (user) {
      await this.loadUserWorkspaces(user.uid);
      
      // Reload current workspace if one is selected
      const currentId = this.currentWorkspaceIdSubject.value;
      if (currentId) {
        await this.setCurrentWorkspace(currentId);
      }
    }
  }

  /**
   * Check if workspace service is initialized
   * @returns boolean True if a workspace is currently selected
   */
  isInitialized(): boolean {
    return this.initialized && this.currentWorkspaceIdSubject.value !== null;
  }

  /**
   * Update workspace statistics (property count, contract count, etc.)
   * @param workspaceId The workspace ID to update stats for
   * @returns Observable with the updated stats
   */
  updateWorkspaceStats(workspaceId: string): Observable<UpdateWorkspaceStatsResponse> {
    const updateStatsFunction = httpsCallable(this.functions, 'updateWorkspaceStats');
    return new Observable<UpdateWorkspaceStatsResponse>(observer => {
      updateStatsFunction({ workspaceId })
        .then(result => {
          observer.next(result.data as UpdateWorkspaceStatsResponse);
          observer.complete();
        })
        .catch(error => {
          observer.error(error);
        });
    });
  }

  /**
   * Read-only impact preview: how many cross-workspace relationships
   * (tenants / providers / co-ownership) a deletion of this workspace would
   * terminate. Owner only. Surfaced in the delete dialog before confirming.
   */
  async getWorkspaceDeletionImpact(
    workspaceId: string
  ): Promise<WorkspaceDeletionImpact | null> {
    const fn = httpsCallable<{ workspaceId: string }, WorkspaceDeletionImpactResponse>(
      this.functions,
      'getWorkspaceDeletionImpact'
    );
    try {
      const result = await fn({ workspaceId });
      return result.data?.impact ?? null;
    } catch (error: unknown) {
      this.logger.error('Error computing workspace deletion impact:', this.CONTEXT, error);
      throw error;
    }
  }

  /**
   * Soft-delete a workspace: marks it deleted with a 30-day recovery window
   * (a super admin can restore it before permanent purge). Owner only.
   * @param workspaceId The workspace ID to delete
   * @param confirmationName Must match the workspace name exactly
   */
  async softDeleteWorkspace(
    workspaceId: string,
    confirmationName: string
  ): Promise<SoftDeleteWorkspaceResponse> {
    const deleteFunction = httpsCallable<
      { workspaceId: string; confirmationName: string },
      SoftDeleteWorkspaceResponse
    >(this.functions, 'deleteWorkspace');

    try {
      const result = await deleteFunction({ workspaceId, confirmationName });

      // Drop the current workspace if it was the one just deleted.
      if (this.currentWorkspaceIdSubject.value === workspaceId) {
        this.clearWorkspace();
      }

      const user = this.auth.currentUser;
      if (user) {
        await this.loadUserWorkspaces(user.uid);
      }

      return result.data as SoftDeleteWorkspaceResponse;
    } catch (error: unknown) {
      this.logger.error('Error soft-deleting workspace:', this.CONTEXT, error);
      throw error;
    }
  }

  /**
   * Permanently delete a workspace and all its data
   * This is an irreversible action that can only be performed by the workspace owner
   * @param workspaceId The workspace ID to delete
   * @param confirmationText Must be exactly "DELETE" to confirm
   * @returns Promise with deletion result
   */
  async permanentlyDeleteWorkspace(
    workspaceId: string,
    confirmationText: string
  ): Promise<PermanentlyDeleteWorkspaceResponse> {
    const deleteFunction = httpsCallable(this.functions, 'permanentlyDeleteWorkspace');

    try {
      const result = await deleteFunction({
        workspaceId,
        confirmationText
      });

      // If successful, clear current workspace if it was the deleted one
      if (this.currentWorkspaceIdSubject.value === workspaceId) {
        this.clearWorkspace();
      }

      // Reload user's workspaces
      const user = this.auth.currentUser;
      if (user) {
        await this.loadUserWorkspaces(user.uid);
      }

      return result.data as PermanentlyDeleteWorkspaceResponse;
    } catch (error: unknown) {
      this.logger.error('Error deleting workspace:', this.CONTEXT, error);
      throw error;
    }
  }

  /**
   * Get user workspaces as membership models (for UI compatibility)
   * @returns Promise<WorkspaceMembershipModel[]>
   */
  async getUserWorkspacesMemberships(): Promise<WorkspaceMembershipModel[]> {
    this.ensureInitialized(); // Lazy initialization
    const workspaces = this.userWorkspacesSubject.value;
    const user = this.auth.currentUser;
    const userId = user?.uid || '';
    
    return workspaces.map(workspace => {
      const member = workspace.members[userId];
      return new WorkspaceMembershipModel({
        workspaceId: workspace.id,
        workspaceName: workspace.name,
        // The service's `Workspace.type` is stored loosely as `string`;
        // this cast narrows to the canonical union used by consumers.
        workspaceType: (workspace.type || 'personal') as WorkspaceType,
        workspaceColor: workspace.color, // Include workspace color
        workspaceIcon: workspace.icon, // Include workspace icon
        role: (member?.role || 'viewer') as WorkspaceRole,
        ownerId: workspace.ownerId,
        ownerEmail: workspace.ownerEmail || '',
        joinedAt: member?.joinedAt || new Date(),
        lastAccessedAt: new Date()
      });
    });
  }
}
